/**
 * Firestore document shapes for the v2 app. All denormalized so reads
 * happen without joins — write paths (server actions / Cloud Functions)
 * are responsible for refreshing the embedded display fields when their
 * sources change.
 *
 * Conventions:
 *   - Timestamps are stored as Firestore `Timestamp` server-side but
 *     surface as `Date` after fetch (Firestore SDK auto-converts).
 *   - Money / distance / ratio scalars are plain `number`.
 *   - Collection / doc IDs use natural keys where possible (icao, code,
 *     aircraft id) so cross-references are stable across migrations.
 */

import type { Timestamp } from "firebase/firestore";

// ============================================================
// Usuarios — id == Firebase Auth UID
// ============================================================
export interface UsuarioDoc {
  email: string;
  displayName: string;
  initials: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isDeleted: boolean;
  /** Role tags: e.g. "pilot", "requester". Admin is its own boolean. */
  roles: string[];
  /** Pre-filled at /solicitar; null if the user hasn't been assigned one. */
  defaultAccountCode: string | null;
  /**
   * Accounts this user is allowed to bill. When null / missing /
   * empty, the user can use any active account (legacy behavior for
   * unconfigured users). When set, /solicitar's Cuenta dropdown is
   * restricted to this subset.
   */
  accountCodes?: string[] | null;
  /** Most-recent client-side session start. Updated by AuthProvider on
   * each auth-state change so admins can see who's still active. */
  lastSignInAt?: Timestamp | null;
  /** Running count of failed email deliveries to this user's address.
   * Bumped by `sendEmail` on Gmail send errors. Surfaces a chip in
   * /admin/users so admin can spot bad addresses. */
  bounceCount?: number;
  lastBounceAt?: Timestamp | null;
  /** Pilot credentials — used to auto-fill the Manifiesto PDF when
   * the assigned pilot is this user. Optional; only meaningful for
   * users with the "pilot" role tag. */
  licenseDgac?: string | null;
  licenseFaa?: string | null;
  /** Which license number(s) to print on the Manifiesto when the pilot
   * has both. Missing/null = "dgac" (legacy behavior). */
  manifestLicense?: "dgac" | "faa" | "both" | null;
  rut?: string | null;
  /** The Pasajeros/{id} that IS this person, set by an admin. Lets the
   * Claude assistant resolve "el pasajero soy yo" to the right passenger
   * record. Null/missing = not linked. */
  passengerId?: string | null;
  authUid: string;
  createdAt?: Timestamp;
}

// ============================================================
// Aviones — id is the natural code: "cirrus" | "pilatus"
// ============================================================
export interface AvionDoc {
  displayName: string;
  /** Aircraft registration / tail number, e.g. "CC-AAB". Optional — set
   * once there's more than one of a given model so the fleet can be
   * disambiguated. Free-text; uppercase by convention. */
  tailNumber: string | null;
  speedKt: number;
  homeBaseIcao: string;
  maxPax: number;
  active: boolean;
  /** Curated short list of "common" ICAOs for this aircraft, shown at
   * the top of the LocationCombobox dropdown on Solicitar so the
   * usual destinations are a one-tap pick. Admin-maintained from
   * /admin/aircraft; can be seeded from a "top used last 12 months"
   * computation on demand. */
  topAirports?: string[] | null;
  createdAt?: Timestamp;
}

/** Subcollection: Aviones/{aircraftId}/Blackouts/{autoId} */
export interface BlackoutDoc {
  /** Half-open [startAt, endAt). */
  startAt: Timestamp;
  endAt: Timestamp;
  reason: string | null;
  createdAt?: Timestamp;
}

// ============================================================
// Cuentas — id == account code (e.g., "AJB", "CJB")
// ============================================================
export interface CuentaDoc {
  code: string;
  displayName: string;
  /** Email addresses billed reports CC to (comma-split on write). */
  contactEmails: string[];
  active: boolean;
  createdAt?: Timestamp;
}

// ============================================================
// Aeropuertos — id == ICAO (4-char, sigil-padded `_XXX` for 3-char codes)
// ============================================================
export interface AeropuertoDoc {
  /** Short name of the field, e.g., "Vitacura". */
  displayName: string;
  /** City or town the airport serves, e.g., "Santiago". */
  city: string | null;
  /** "Vitacura (SCLC)" — the display string used everywhere. */
  displayCombined: string;
  lat: number | null;
  lon: number | null;
  active: boolean;
  /** Con quién se activa/cancela el plan de vuelo en aeródromos sin torre,
   * texto libre para ForeFlight: "Santiago en 12240". Vacío si tiene torre. */
  fplContacto?: string | null;
  /** True when auto-created from user free-text and not yet admin-curated. */
  isProvisional: boolean;
  createdAt?: Timestamp;
}

// ============================================================
// Distancias — id is the canonical sorted pair "LO|HI" (e.g., "SCEL|SCLC")
// ============================================================
export interface DistanciaDoc {
  /** Lexicographically smaller of the two ICAOs. */
  lo: string;
  /** Lexicographically larger of the two ICAOs. */
  hi: string;
  distanceNm: number;
  /** "manual" | "data_distancias_import" (legacy: "gemini" — frozen). */
  source: string;
  lastVerifiedAt: Timestamp;
  createdAt?: Timestamp;
}

/** Build the canonical Distancias doc id from any two ICAOs. */
export function distanciaId(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

// ============================================================
// Pasajeros — autoId
// ============================================================
export interface PasajeroDoc {
  fullName: string;
  family: string | null;
  rut: string | null;
  dateOfBirth: string | null; // YYYY-MM-DD
  active: boolean;
  createdAt?: Timestamp;
}

// ============================================================
// Vuelos — autoId; heavily denormalized for read-without-join
// ============================================================
export type FlightStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "completed";

export type Disposition = "home" | "stay" | "relocate_other";

export interface FlightPaxEntry {
  /** Pasajeros/{passengerId} if from directory; null for ad-hoc entries. */
  passengerId: string | null;
  fullName: string;
  family: string | null;
  rut: string | null;
}

export interface VueloDoc {
  /** Public-facing "26-001" id; assigned by submitFlight Cloud Function. */
  publicNumber: string;
  /** Legacy v1 flight_id (bigint as string) for users who came from v1. */
  legacyFlightId: string | null;
  status: FlightStatus;

  /** Local Santiago calendar date "YYYY-MM-DD" for cheap range queries. */
  fecha: string;
  /** Local Santiago wall-clock "HH:MM". */
  hora: string;
  /** Authoritative timestamps in UTC. */
  startAt: Timestamp;
  endAt: Timestamp;

  aircraftId: string; // -> Aviones doc id
  aircraftDisplayName: string;

  originIcao: string;
  originDisplay: string;
  destinationIcao: string;
  destinationDisplay: string;

  distanceNm: number | null;
  flightTimeMin: number | null;

  numPax: number;
  pax: FlightPaxEntry[];

  accountCode: string | null;

  /** Firebase Auth UID of the requester (== Usuarios doc id). */
  requesterUid: string;
  requesterDisplayName: string;
  requesterEmail: string | null;

  /** Pilot suggested at submission time. */
  pilotUidRequested: string | null;
  pilotDisplayNameRequested: string | null;
  /** Pilot actually assigned at approval. */
  pilotUid: string | null;
  pilotDisplayName: string | null;

  postFlightDisposition: Disposition | null;
  postFlightDestinationIcao: string | null;

  notes: string | null;
  /** Token preserved for legacy /v/[id]?t= deep links; will be unused in v2 itself. */
  editToken: string;

  googleCalendarEventId: string | null;
  preFerryCalendarEventId: string | null;
  postFerryCalendarEventId: string | null;

  /** Set when the row references provisional locations or missing distance. */
  needsModeration: boolean;

  /** When the flight is a return of another flight, points back. */
  returnOfFlightId: string | null;

  /** Frozen copy of what the user submitted (for the approval-email diff). */
  submittedSnapshot: Record<string, unknown> | null;
  /** Frozen "original vs requested" pair when a user-edit re-pended it. */
  modificationRequest: Record<string, unknown> | null;

  createdAt: Timestamp;
  updatedAt: Timestamp;
  approvedAt: Timestamp | null;
  approvedByUid: string | null;
  isTest: boolean;
  /** Client-side UUID from the submit form, used for idempotent
   * retries. Null on legacy docs. */
  clientToken?: string | null;
  /** Resumen de NOTAMs del día del vuelo (server-write: getFlightNotams /
   * remindersT24). Ausente hasta la primera consulta. */
  notams?: FlightNotams | null;
  /** Clima esperado (server-write, mismo ciclo que `notams`). */
  clima?: FlightWeather | null;
}

// ============================================================
// Clima esperado — Vuelos/{id}.clima (ver functions/weather.js)
// ============================================================
export type WeatherSeveridad = "normal" | "atencion" | "adverso";

export interface FlightWeatherAerodrome {
  icao: string;
  clima: string;
  taf: string;
  tafNota: string;
}

export interface FlightWeather {
  updatedAt: Timestamp;
  fetchedAt: string;
  salidaLocal: string;
  llegadaLocal: string;
  resumen: string;
  severidad: WeatherSeveridad;
  avisos: { texto: string; referencia: string; vigencia: string }[];
  origen: FlightWeatherAerodrome;
  destino: FlightWeatherAerodrome;
  ruta: { vientoCrucero: string; condiciones: string; riesgos: string[] };
  model: string;
  firs: string[];
  stats?: Record<string, unknown>;
  fuentes?: {
    sigmets: { fir: string; hazard: string | null; raw: string; validToUtc: string | null }[];
    taf: {
      origen: { station: string; distNm: number; cubreVuelo: boolean; issuedUtc: string | null; raw: string } | null;
      destino: { station: string; distNm: number; cubreVuelo: boolean; issuedUtc: string | null; raw: string } | null;
    };
    metar: { station: string; distNm?: number | null; raw: string; obsTimeUtc: string | null }[];
  };
}

// ============================================================
// NOTAMs — resumen guardado en Vuelos/{id}.notams (ver functions/notams.js)
// ============================================================
export type NotamSeveridad = "afecta" | "precaucion" | "info";
export type NotamGrupo = "origen" | "destino" | "ruta" | "otro";
export type NotamHorario = "durante" | "antes" | "despues" | "todo_el_dia";

export interface FlightNotamItem {
  grupo: NotamGrupo;
  /** ICAO del aeródromo (vacío en ítems de ruta). */
  icao: string;
  severidad: NotamSeveridad;
  /** Titular corto (máx. 8 palabras). Ausente en resúmenes antiguos. */
  titulo?: string;
  /** Una frase corta con dónde y qué implica. */
  texto: string;
  /** Sub-puntos cuando el ítem agrupa varias cosas. */
  detalles?: string[];
  /** Números de NOTAM, ej. "C3903/26". */
  referencias: string[];
  /** Vigencia en hora local, ej. "hoy 09:00–18:00". */
  vigencia: string;
  horario: NotamHorario;
  /** Aviso cuando rige antes/después del horario del vuelo. */
  notaHorario: string;
}

export interface FlightNotams {
  updatedAt: Timestamp;
  /** ISO de la consulta al IFIS. */
  fetchedAt: string;
  resumen: string;
  items: FlightNotamItem[];
  descartados: number;
  motivoDescartes: string;
  candidatos: number;
  model: string;
  firs: string[];
  /** Aeródromos del vuelo fuera de Chile: sus NOTAMs NO se revisan. */
  foreignAerodromes?: string[];
  /** Consultas al IFIS que fallaron (resultado parcial). */
  warnings?: string[];
  aerodromes: { icao: string; name: string | null; total: number }[];
  stats?: Record<string, unknown>;
  /** Texto original de los NOTAMs referenciados, por número. */
  fuentes?: Record<string, NotamFuente>;
}

export interface NotamFuente {
  aerodromo: string | null;
  q: string | null;
  /** Descripción del código Q según el IFIS. */
  codigo: string | null;
  /** Horario D) tal cual (UTC). */
  d: string | null;
  e: string;
  f: string | null;
  g: string | null;
  inicio: string | null;
  /** ISO o "PERM". */
  fin: string | null;
  vigencia: string | null;
  ubicacion: string | null;
}

// ============================================================
// Notificaciones — autoId, server-write only
// ============================================================
export type NotificationKind =
  | "approval_request"
  | "confirmation"
  | "rejection"
  | "modification"
  | "cancellation"
  | "reminder_t24"
  | "reminder_t24_pilot"
  | "pilot_update_t6"
  | "admin_nudge"
  | "conflict_inquiry"
  | "pilot_assigned"
  | "legacy_merge";

export type NotificationStatus = "queued" | "sent" | "failed" | "bounced";

export interface NotificacionDoc {
  flightId: string | null; // -> Vuelos doc id
  kind: NotificationKind;
  channel: "email" | "whatsapp";
  toAddress: string;
  templateId: string | null;
  status: NotificationStatus;
  providerMessageId: string | null;
  errorDetail: string | null;
  sentAt: Timestamp | null;
  createdAt: Timestamp;
}

// ============================================================
// Counters — id "flight_{YYYY}"; server-only
// ============================================================
export interface FlightCounterDoc {
  year: number;
  nextNumber: number;
  updatedAt: Timestamp;
}
